import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useTheme } from "../context/ThemeContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Printer, Bluetooth, Usb, ArrowLeft, QrCode, Send, Share2, Image as ImgIcon, Download, MessageSquare, FileImage, WifiOff } from "lucide-react";
import { toast } from "sonner";
import { connectBluetooth, connectUSB, printReceipt, buildReceiptText, BIG_MARK, HUGE_MARK, SMALL_MARK, stripBigMarks } from "../lib/printer";
import { getQueued } from "../lib/offlineQueue";
import html2canvas from "html2canvas";

const fmtIDR = (n) => "Rp" + Number(n||0).toLocaleString("id-ID");

export default function Receipt() {
  const { rentalId } = useParams();
  const { settings } = useTheme();
  const nav = useNavigate();
  const [rental, setRental] = useState(null);
  const [width, setWidth] = useState(32);
  const [connected, setConnected] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const receiptRef = useRef();

  useEffect(() => {
    if (settings?.receipt_paper_width) setWidth(Number(settings.receipt_paper_width));
  }, [settings]);

  useEffect(() => {
    if (rentalId?.startsWith("local-")) {
      getQueued(rentalId).then(rec => { if (rec?.receipt) setRental(rec.receipt); }).catch(() => {});
    } else {
      api.get(`/rentals/${rentalId}`).then(r => setRental(r.data)).catch(() => {});
    }
  }, [rentalId]);

  const connectBT = async () => {
    try { const c = await connectBluetooth(); setConnected(c); toast.success("Bluetooth: " + c.name); }
    catch (e) { toast.error(e.message); }
  };
  const connectUsb = async () => {
    try { const c = await connectUSB(); setConnected(c); toast.success("USB: " + c.name); }
    catch (e) { toast.error(e.message); }
  };

  const doPrint = async () => {
    if (!connected) return toast.error("Hubungkan printer dulu (Bluetooth atau USB)");
    try {
      await printReceipt(connected, rental, settings, width);
      toast.success("Cetak terkirim");
    } catch (e) { toast.error("Gagal cetak: " + e.message); }
  };

  const captureAsImage = async () => {
    if (!receiptRef.current) return null;
    const canvas = await html2canvas(receiptRef.current, { scale: 2, backgroundColor: "#fff", useCORS: true });
    return canvas;
  };

  const downloadImage = async () => {
    try {
      const canvas = await captureAsImage();
      if (!canvas) return;
      const url = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.download = `struk-${rental.code}.png`;
      link.href = url; link.click();
      toast.success("Gambar struk diunduh");
    } catch (e) { toast.error("Gagal: " + e.message); }
  };

  const shareAsImage = async () => {
    setShareOpen(false);
    if (!rental.customer_whatsapp) return toast.error("Pelanggan tidak punya nomor WhatsApp");
    try {
      const canvas = await captureAsImage();
      if (!canvas) return;
      if (navigator.canShare) {
        canvas.toBlob(async (blob) => {
          const file = new File([blob], `struk-${rental.code}.png`, { type: "image/png" });
          const shareData = {
            files: [file],
            title: `Struk ${rental.code}`,
            text: `Struk dari ${settings.business_name}\nTotal: ${fmtIDR(rental.total)}`,
          };
          if (navigator.canShare(shareData)) {
            try {
              await navigator.share(shareData);
              toast.success("Menu bagikan terbuka");
              return;
            } catch (e) {
              if (e.name !== "AbortError") console.error(e);
            }
          }
          fallbackWa();
        });
      } else {
        fallbackWa();
      }
    } catch (e) { toast.error("Gagal: " + e.message); }
  };

  const shareAsText = () => {
    setShareOpen(false);
    if (!rental.customer_whatsapp) return toast.error("Pelanggan tidak punya nomor WhatsApp");
    const wa = rental.customer_whatsapp.replace(/^0/, "62").replace(/[^\d]/g, "");
    // Bungkus dengan blok monospace WhatsApp menggunakan ``` supaya tetap rapi
    const receiptStr = stripBigMarks(buildReceiptText(rental, settings, width)).trimEnd();
    const msg = `Halo *${rental.customer_name}*, berikut struk transaksi Anda dari *${settings.business_name}*:\n\n\`\`\`\n${receiptStr}\n\`\`\`\n\nTerima kasih!`;
    window.open(`https://wa.me/${wa}?text=${encodeURIComponent(msg)}`, "_blank");
    toast.success("Membuka WhatsApp dengan teks struk");
  };

  const fallbackWa = async () => {
    await downloadImage();
    const wa = rental.customer_whatsapp.replace(/^0/, "62").replace(/[^\d]/g, "");
    const msg = encodeURIComponent(`Halo ${rental.customer_name}, berikut struk transaksi *${rental.code}* dari ${settings.business_name}. Total: ${fmtIDR(rental.total)}.\n\nGambar struk sudah terunduh, silakan lampirkan di chat ini.`);
    window.open(`https://wa.me/${wa}?text=${msg}`, "_blank");
    toast.info("Buka WhatsApp — gambar sudah diunduh, tinggal lampirkan");
  };

  const sendSurveyLink = async () => {
    if (!rental.customer_whatsapp) return toast.error("Pelanggan tidak punya nomor WhatsApp");
    const link = `${window.location.origin}/survey/${rentalId}`;
    const msg = `Halo ${rental.customer_name}, terima kasih telah menyewa di ${settings.business_name}. Mohon berikan penilaian di: ${link}`;
    try {
      const r = await api.post("/whatsapp/send", { target: rental.customer_whatsapp, message: msg });
      if (r.data.mocked) toast.info("Mode simulasi. Link: " + link);
      else toast.success("Link survei terkirim");
    } catch { toast.error("Gagal kirim"); }
  };

  if (!rental || !settings) return <div className="p-8">Memuat...</div>;
  const previewLines = buildReceiptText(rental, settings, width).split("\n");
  const surveyUrl = `${window.location.origin}/survey/${rentalId}`;
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(surveyUrl)}`;

  return (
    <div className="min-h-screen bg-neutral-50 p-6" data-testid="receipt-page">
      <div className="max-w-5xl mx-auto">
        {rental.offline && (
          <div data-testid="receipt-offline-badge" className="mb-4 rounded-md bg-amber-50 border border-amber-300 text-amber-800 text-xs font-semibold px-4 py-2 flex items-center gap-2 no-print">
            <WifiOff className="w-3.5 h-3.5 shrink-0" /> Struk offline · transaksi ini akan otomatis tersinkron ke server saat internet kembali.
          </div>
        )}
        <div className="flex items-center justify-between mb-6 no-print flex-wrap gap-3">
          <Button variant="outline" onClick={() => nav(-1)}><ArrowLeft className="w-4 h-4 mr-1" /> Kembali</Button>
          <div className="flex gap-2 flex-wrap">
            <select value={width} onChange={e => setWidth(Number(e.target.value))} className="border border-neutral-200 rounded px-3 text-sm" data-testid="receipt-width">
              <option value={32}>58mm (32 char)</option>
              <option value={48}>80mm (48 char)</option>
            </select>
            <Button data-testid="bt-connect" variant="outline" onClick={connectBT}><Bluetooth className="w-4 h-4 mr-1" /> Bluetooth</Button>
            <Button data-testid="usb-connect" variant="outline" onClick={connectUsb}><Usb className="w-4 h-4 mr-1" /> USB</Button>
            {connected && <span className="text-xs px-2 py-1 rounded bg-green-50 text-green-800 border border-green-200 flex items-center">Tersambung: {connected.name}</span>}
            <Button data-testid="bt-print" onClick={doPrint} className="text-white" style={{background: "var(--brand-primary)"}}><Printer className="w-4 h-4 mr-1" /> Cetak</Button>
            <Button data-testid="share-wa" onClick={() => setShareOpen(true)} variant="outline"><Share2 className="w-4 h-4 mr-1" /> Bagikan ke WA</Button>
            <Button data-testid="download-img" variant="outline" onClick={downloadImage}><Download className="w-4 h-4 mr-1" /> Unduh PNG</Button>
            <Button data-testid="send-survey" variant="outline" onClick={sendSurveyLink}><Send className="w-4 h-4 mr-1" /> Kirim Survei</Button>
            <Button variant="outline" onClick={() => window.print()}><Printer className="w-4 h-4 mr-1" /> Print Browser</Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <Card className="border-neutral-200 shadow-none">
            <CardContent className="p-6">
              <h3 className="font-display font-semibold text-lg mb-4">Ringkasan Transaksi</h3>
              <dl className="text-sm space-y-2">
                <div className="flex justify-between border-b border-neutral-100 pb-2"><dt className="text-neutral-500">Kode</dt><dd className="font-mono-tab font-semibold">{rental.code}</dd></div>
                <div className="flex justify-between border-b border-neutral-100 pb-2"><dt className="text-neutral-500">Pelanggan</dt><dd>{rental.customer_name}</dd></div>
                <div className="flex justify-between border-b border-neutral-100 pb-2"><dt className="text-neutral-500">Periode</dt><dd>{rental.start_date} → {rental.end_date} ({rental.days}h)</dd></div>
                <div className="flex justify-between border-b border-neutral-100 pb-2"><dt className="text-neutral-500">Subtotal</dt><dd className="font-mono-tab">{fmtIDR(rental.subtotal)}</dd></div>
                <div className="flex justify-between border-b border-neutral-100 pb-2"><dt className="text-neutral-500">Diskon {rental.discount_type === "persen" ? `(${rental.discount}%)` : ""}</dt><dd className="font-mono-tab">-{fmtIDR(rental.discount_amount || rental.discount)}</dd></div>
                <div className="flex justify-between border-b border-neutral-100 pb-2"><dt className="text-neutral-500">Jaminan</dt><dd className="font-mono-tab">{fmtIDR(rental.deposit)}</dd></div>
                <div className="flex justify-between pt-2"><dt className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">Total</dt><dd className="font-display font-black text-3xl font-mono-tab" style={{color: "var(--brand-primary)"}} data-testid="receipt-total">{fmtIDR(rental.total)}</dd></div>
                {rental.dp_amount > 0 && (
                  <>
                    <div className="flex justify-between border-t border-neutral-100 pt-2"><dt className="text-neutral-500">DP Dibayar ({rental.dp_type === "persen" ? `${rental.dp_value}%` : "Rp"})</dt><dd className="font-mono-tab">-{fmtIDR(rental.dp_amount)}</dd></div>
                    <div className="flex justify-between"><dt className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">Sisa Pelunasan</dt><dd className="font-display font-black text-2xl font-mono-tab" data-testid="receipt-remaining">{fmtIDR(rental.remaining)}</dd></div>
                  </>
                )}
              </dl>
              {settings.receipt_show_qr && (
                <div className="mt-6 flex items-center gap-3 p-4 border border-neutral-200 rounded">
                  <img src={qrSrc} alt="QR Survei" className="w-24 h-24" />
                  <div>
                    <div className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">Survei Kepuasan</div>
                    <div className="font-mono-tab text-xs break-all mt-1">{surveyUrl}</div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-neutral-200 shadow-none">
            <CardContent className="p-6">
              <h3 className="font-display font-semibold text-lg mb-4 flex items-center gap-2"><QrCode className="w-4 h-4" /> Pratinjau Struk</h3>
              <div id="receipt-print" ref={receiptRef} className="receipt-preview mx-auto" style={{ width: width === 32 ? 240 : 340 }}>
                {settings.receipt_show_logo !== false && settings.logo_base64 && (
                  <div className="flex justify-center mb-2">
                    <img src={settings.logo_base64} alt="logo" style={{ maxWidth: 120, filter: "grayscale(100%) contrast(1.5)" }} />
                  </div>
                )}
                <pre className="whitespace-pre-wrap text-[11px] leading-tight" style={{fontFamily: "'JetBrains Mono',monospace"}} data-testid="receipt-preview-text">
                  {previewLines.map((ln, i) => ln.startsWith(HUGE_MARK)
                    ? <span key={i} className="block font-black text-[26px] leading-tight text-center" data-testid="receipt-huge-line">{ln.slice(1)}{"\n"}</span>
                    : ln.startsWith(BIG_MARK)
                    ? <span key={i} className="block font-black text-[19px] leading-snug" data-testid="receipt-big-line">{ln.slice(1)}{"\n"}</span>
                    : ln.startsWith(SMALL_MARK)
                    ? <span key={i} className="block font-black text-[14px] leading-snug" data-testid="receipt-small-line">{ln.slice(1)}{"\n"}</span>
                    : <span key={i}>{ln}{"\n"}</span>)}
                </pre>
                {settings.receipt_show_qr !== false && (
                  <div className="flex justify-center mt-2">
                    <img src={qrSrc} alt="QR" style={{ width: 90, height: 90 }} />
                  </div>
                )}
              </div>
              <p className="text-xs text-neutral-500 mt-4 text-center flex items-center justify-center gap-1"><ImgIcon className="w-3 h-3" /> Struk ini dapat dibagikan sebagai gambar via tombol "Bagikan ke WA".</p>
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="max-w-sm" data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle>Bagikan Struk ke WhatsApp</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-neutral-500 -mt-2">Pilih format pengiriman struk ke {rental.customer_name}.</p>
          <div className="grid grid-cols-1 gap-2 mt-2">
            <button
              data-testid="share-as-image"
              onClick={shareAsImage}
              className="w-full text-left border border-neutral-200 rounded-md p-4 hover:bg-neutral-50 transition-colors flex items-start gap-3"
            >
              <FileImage className="w-6 h-6 shrink-0 mt-0.5" style={{ color: "var(--brand-primary)" }} />
              <div>
                <div className="font-display font-semibold text-sm">Kirim sebagai Gambar</div>
                <div className="text-xs text-neutral-500 mt-0.5">Struk dirender menjadi PNG lalu dibagikan lewat Web Share atau diunduh untuk dilampirkan manual.</div>
              </div>
            </button>
            <button
              data-testid="share-as-text"
              onClick={shareAsText}
              className="w-full text-left border border-neutral-200 rounded-md p-4 hover:bg-neutral-50 transition-colors flex items-start gap-3"
            >
              <MessageSquare className="w-6 h-6 shrink-0 mt-0.5" style={{ color: "var(--brand-primary)" }} />
              <div>
                <div className="font-display font-semibold text-sm">Kirim sebagai Teks</div>
                <div className="text-xs text-neutral-500 mt-0.5">Teks struk (monospace) langsung terisi di chat WhatsApp — pelanggan tinggal kirim.</div>
              </div>
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
